import React, { useId, useState } from 'react';
import type { Nullable } from 'corgi-types';
import { HomeClubSectionFlow, QueryParam, SignupFlow } from 'corgi-types';

import HomeClubSubscriptionModal from 'common/components/HomeClubSubscriptionModal';
import { useFormSubmit } from 'common/hooks/home-club/use-form-submit';
import { useToggleModal } from 'common/hooks/toggle-modal';
import { useLocalization } from 'common/providers/LocaleProvider';
import {
  dispatchHomeClubSectionClickEvent,
  HomeClubSectionEventElement,
  HomeClubSectionEventInteraction,
  HomeClubSectionEventPlacement,
} from 'common/tracking/home-club';
import type { BaseComponentProps } from '../types';
import {
  getIsBirthdayCardHidden,
  setIsBirthdayCardHidden as setIsBirthdayCardHiddenInLocalStorage,
} from './BirthdayCard/utils/local-storage';
import HomeClubContent from './HomeClubContent/HomeClubContent';
import HomeClubMemberContent from './HomeClubContent/HomeClubMemberContent';
import HomeClubLogoSection from './HomeClubLogoSection';

export interface HomeClubSectionProps extends BaseComponentProps {
  renderWithOpenModal: boolean;
  hasPreviousSubAttemptFailed: boolean;
  flow?: HomeClubSectionFlow;
  homeClubId?: string;
  withBirthdayCard?: boolean;
}

const HomeClubSection = ({
  isLoggedIn,
  trackingProps,
  renderWithOpenModal,
  withBirthdayCard = false,
  hasPreviousSubAttemptFailed,
  homeClubId: initialHomeClubId,
  flow = HomeClubSectionFlow.Content,
}: HomeClubSectionProps) => {
  const { t } = useLocalization();
  const [errorText, setErrorText] = useState<Nullable<string>>(null);
  const [successText, setSuccessText] = useState<Nullable<string>>(null);
  const [homeClubId, setHomeClubId] = useState(initialHomeClubId);
  const [isBirthdayCardHidden, setIsBirthdayCardHidden] = useState(false);
  const { isModalOpen, handleModalOpen, handleModalClose } = useToggleModal(
    renderWithOpenModal && isLoggedIn
  );
  const headingId = useId();
  const isHomeClubMember = !!homeClubId;
  const buttonLabel = t('h24_hc_submit_btn_label');
  const onSuccess = (newHomeClubId: string) => {
    if (flow === HomeClubSectionFlow.Cart || flow === HomeClubSectionFlow.CartRedirect) {
      window.location.href =
        t('h24_cart_url') + `?${QueryParam.UserFlow}=${SignupFlow.CartHomeClub}`;
    } else {
      setHomeClubId(newHomeClubId);
    }
  };
  const { handleSubmit, isLoading } = useFormSubmit({
    tracking: { buttonLabel, ...trackingProps },
    setErrorText,
    setSuccessText,
    onSuccess,
    withSuccessDelay: withBirthdayCard,
  });
  const commonProps = { isLoading, errorText, trackingProps, handleSubmit };

  if (isHomeClubMember) {
    const showBirthdayCard =
      withBirthdayCard &&
      successText !== t('h24_hc_birthday_success_msg') &&
      !getIsBirthdayCardHidden() &&
      !isBirthdayCardHidden;

    const onBirthdayCardClose = () => {
      setIsBirthdayCardHiddenInLocalStorage();
      setIsBirthdayCardHidden(true);
      dispatchHomeClubSectionClickEvent({
        element: HomeClubSectionEventElement.Icon,
        interaction: HomeClubSectionEventInteraction.Close,
        placement: HomeClubSectionEventPlacement.Inline,
      });
    };

    if (flow === HomeClubSectionFlow.Content || flow === HomeClubSectionFlow.CartRedirect) {
      return (
        <HomeClubMemberContent
          homeClubId={homeClubId}
          successText={successText}
          setSuccessMessage={setSuccessText}
          showBirthdayCard={showBirthdayCard}
          onBirthdayCardClose={onBirthdayCardClose}
        />
      );
    }

    return <HomeClubLogoSection homeClubId={homeClubId} />;
  }

  return (
    <section data-corgi-id="home-club-section" aria-labelledby={headingId}>
      <HomeClubSubscriptionModal
        isModalOpen={isModalOpen}
        buttonLabel={buttonLabel}
        handleModalClose={handleModalClose}
        {...commonProps}
      />
      <HomeClubContent
        flow={flow}
        isLoggedIn={isLoggedIn}
        hasPreviousSubAttemptFailed={hasPreviousSubAttemptFailed}
        setErrorText={setErrorText}
        handleModalOpen={handleModalOpen}
        headingId={headingId}
        {...commonProps}
      />
    </section>
  );
};
export default HomeClubSection;
